import { useQuery } from '@tanstack/react-query';
import { Download, ExternalLink, FileText, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui';
import { onboardingService } from '../services/onboardingService';
import type { DocumentEntityType } from '../form-builder/types';

/**
 * Lightbox-style modal that fetches a presigned URL for a stored document
 * and renders it inline. Images render as `<img>`, PDFs as `<iframe>`,
 * everything else falls back to a download link. Uses the existing
 * versioned-document API (`GET .../document/:entity_type/:entity_id/version/:version_id`).
 */

interface DocumentPreviewModalProps {
  entityType?: DocumentEntityType;
  entityId: number | string;
  versionId: number | null;
  /** Optional label shown in the header (e.g. "PAN Card"). */
  title?: string;
  onClose: () => void;
}

export const DocumentPreviewModal = ({
  entityType = 'case',
  entityId,
  versionId,
  title,
  onClose,
}: DocumentPreviewModalProps) => {
  const isOpen = versionId !== null && versionId > 0;

  const detailsQuery = useQuery({
    queryKey: ['document-version-preview', entityType, entityId, versionId],
    queryFn: () =>
      onboardingService.getDocumentVersion({
        entityType,
        entityId,
        versionId: versionId as number,
      }),
    enabled: isOpen,
    staleTime: 30 * 1000,
    retry: 1,
  });

  if (!isOpen) return null;

  const details = detailsQuery.data;
  const mimetype = (details?.mimetype || '').toLowerCase();
  const isImage = mimetype.startsWith('image/');
  const isPdf = mimetype === 'application/pdf' || mimetype.includes('pdf');

  const handleDownload = () => {
    if (!details?.url) return;
    // `<a download>` honours Content-Disposition when the server sets it,
    // otherwise the browser saves with the URL's filename. We also open
    // in a new tab as a fallback for browsers that strip the download
    // hint on cross-origin URLs.
    const a = document.createElement('a');
    a.href = details.url;
    a.download = title ? `${title}` : '';
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleOpenInNewTab = () => {
    if (!details?.url) return;
    window.open(details.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-rgf-gray-200 px-5 py-3">
          <div className="min-w-0 flex items-center gap-2">
            <FileText className="h-4 w-4 text-rgf-gray-500 flex-shrink-0" />
            <h2 className="text-base font-semibold text-rgf-gray-900 truncate">
              {title || 'Document preview'}
            </h2>
            {details?.status && (
              <span className="ml-2 inline-flex items-center rounded-full bg-rgf-gray-100 px-2 py-0.5 text-[11px] font-medium text-rgf-gray-700">
                {details.status}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              icon={<ExternalLink className="h-3.5 w-3.5" />}
              disabled={!details?.url}
              onClick={handleOpenInNewTab}
            >
              Open
            </Button>
            <Button
              type="button"
              size="sm"
              icon={<Download className="h-3.5 w-3.5" />}
              disabled={!details?.url}
              onClick={handleDownload}
            >
              Download
            </Button>
            <button
              type="button"
              aria-label="Close"
              className="p-1 text-rgf-gray-400 hover:text-rgf-gray-700"
              onClick={onClose}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto bg-rgf-gray-50 p-4">
          {detailsQuery.isLoading ? (
            <div className="flex items-center justify-center py-20 text-rgf-gray-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Loading document…
            </div>
          ) : detailsQuery.error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {(detailsQuery.error as Error)?.message || 'Could not load this document.'}
            </div>
          ) : !details?.url ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              The server didn't return a download URL.
            </div>
          ) : isImage ? (
            <img
              src={details.url}
              alt={title || 'Document'}
              className="block max-w-full mx-auto rounded-md shadow-sm"
              loading="lazy"
            />
          ) : isPdf ? (
            <iframe
              src={details.url}
              title={title || 'Document preview'}
              className="block w-full rounded-md shadow-sm bg-white"
              style={{ height: '72vh' }}
            />
          ) : (
            <div className="rounded-lg border border-rgf-gray-200 bg-white p-6 text-sm text-rgf-gray-700 space-y-3">
              <p>This file type ({details.mimetype || 'unknown'}) can't be previewed inline.</p>
              <p>Use Download or Open in a new tab.</p>
            </div>
          )}

          {details && (
            <div className="mt-3 text-xs text-rgf-gray-500 text-center">
              {details.mimetype}
              {typeof details.file_size_bytes === 'number' &&
                ` • ${formatBytes(details.file_size_bytes)}`}
              {typeof details.ttl_seconds === 'number' &&
                ` • Link valid for ${details.ttl_seconds}s`}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const formatBytes = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};
